import {Stack, TextField} from "@mui/material";
import type {URLLink} from "../../../../../sdo/URLLink.ts";
import {fieldSx, SummaryPill, SummaryPillRow} from "../resolvers/editors/ResolverUtils.tsx";

interface WebReferenceEditorProps {
    draft: URLLink;
    editing: boolean;
    onChange: (updated: URLLink) => void;
}

// Multiline-friendly variant of fieldSx (drops fixed 38px height so TextArea can grow)
const multilineFieldSx = {
    ...fieldSx,
    "& .MuiOutlinedInput-root": {
        ...(fieldSx as Record<string, unknown>)["& .MuiOutlinedInput-root"] as object,
        height: "auto",
        padding: "10px 12px",
    },
    "& .MuiOutlinedInput-input": {
        color: "#f2f5fa",
        fontSize: "0.80rem",
        lineHeight: 1.6,
    },
};

function hasText(s: string | undefined | null): boolean {
    return !!(s && s.trim().length > 0);
}

function WebReferenceEditor({draft, editing, onChange}: WebReferenceEditorProps) {
    const upd = <K extends keyof URLLink>(key: K, value: URLLink[K]) => {
        onChange({...draft, [key]: value});
    };

    if (!editing) {
        return (
            <Stack direction="column" spacing={0.5} sx={{width: "100%", maxWidth: 760, mx: "auto", alignItems: "stretch"}}>
                <SummaryPillRow>
                    <SummaryPill label="Source URL" value={hasText(draft.sourceURL) ? draft.sourceURL : "—"}/>
                </SummaryPillRow>
                {hasText(draft.summary) && (
                    <SummaryPillRow>
                        <SummaryPill label="Summary" value={draft.summary}/>
                    </SummaryPillRow>
                )}
            </Stack>
        );
    }

    return (
        <Stack direction="column" spacing={1.5} sx={{width: "100%", maxWidth: 760, mx: "auto", alignItems: "stretch"}}>
            {/* Title — label inside the field */}
            <TextField
                value={draft.title || ""}
                onChange={(e) => upd("title", e.target.value)}
                label="Title"
                size="small"
                fullWidth
                sx={fieldSx}
            />

            {/* Source URL */}
            <TextField
                value={draft.sourceURL || ""}
                onChange={(e) => upd("sourceURL", e.target.value)}
                label="Source URL"
                size="small"
                fullWidth
                sx={fieldSx}
            />

            {/* Summary — multiline */}
            <TextField
                value={draft.summary || ""}
                onChange={(e) => upd("summary", e.target.value)}
                label="Summary"
                size="small"
                fullWidth
                multiline
                rows={6}
                sx={multilineFieldSx}
            />
        </Stack>
    );
}

export default WebReferenceEditor;
